import React from 'react';
import {
  Box,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
  alpha,
  styled,
  useTheme,
} from '@mui/material';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';

/**
 * Unified listings table for Predict.ai. Used by every listings page
 * (Segments, Models, Pipelines, Trainings, Deployments, Inferences)
 * so the navigation, density, sort affordance, hover, status badges,
 * and pagination all match.
 *
 * Caller-provided contract:
 *
 *   columns: [
 *     {
 *       key: string,                    // unique id used for sort + react key
 *       label: string,                  // header text
 *       render?: (row, ctx) => node,    // cell renderer; defaults to row[key]
 *       sortable?: boolean,             // default false
 *       align?: 'left'|'right'|'center',// default 'left'
 *       width?: number|string,          // CSS width
 *       padding?: 'normal'|'none'|'checkbox',
 *       headerSx?: object,              // extra sx on the header cell
 *       cellSx?: object|fn,             // extra sx on the cell (or fn(row))
 *       stopRowClick?: boolean,         // clicks in this column don't trigger onRowClick
 *       hideBelow?: 'sm'|'md'|'lg'|'xl',// hide the column below this breakpoint
 *     },
 *     ...
 *   ]
 *
 * Pagination is server-driven: caller owns page/rowsPerPage state and
 * passes ``totalItems``. Sort is also server-driven: caller owns
 * sortColumn / sortDirection. Both are optional - omit the props and
 * the corresponding affordance disappears.
 */

const HEADER_BG_LIGHT = (theme) =>
  theme.palette.mode === 'dark'
    ? alpha(theme.palette.background.default, 0.6)
    : alpha(theme.palette.text.primary, 0.04);

const StyledHeaderCell = styled(TableCell, {
  shouldForwardProp: (prop) => prop !== 'sortable',
})(({ theme, sortable }) => ({
  fontFamily: theme.typography.fontFamily,
  fontSize: 11,
  fontWeight: 600,
  letterSpacing: 0.4,
  textTransform: 'uppercase',
  color: theme.palette.text.secondary,
  backgroundColor: HEADER_BG_LIGHT(theme),
  borderBottom: `1px solid ${theme.palette.divider}`,
  padding: '10px 12px',
  whiteSpace: 'nowrap',
  userSelect: 'none',
  cursor: sortable ? 'pointer' : 'default',
  '&:first-of-type': { paddingLeft: 16 },
  '&:last-of-type':  { paddingRight: 16 },
  '&:hover': sortable
    ? { color: theme.palette.text.primary }
    : undefined,
}));

const StyledCell = styled(TableCell)(({ theme }) => ({
  fontFamily: theme.typography.fontFamily,
  fontSize: 13,
  color: theme.palette.text.primary,
  padding: '10px 12px',
  borderBottom: `1px solid ${theme.palette.divider}`,
  '&:first-of-type': { paddingLeft: 16 },
  '&:last-of-type':  { paddingRight: 16 },
}));

const StyledRow = styled(TableRow, {
  shouldForwardProp: (prop) => prop !== 'clickable',
})(({ theme, clickable }) => ({
  transition: 'background-color 120ms ease',
  cursor: clickable ? 'pointer' : 'default',
  '&:last-child td': { borderBottom: 0 },
  '&:hover': clickable
    ? {
        backgroundColor:
          theme.palette.mode === 'dark'
            ? alpha(theme.palette.primary.main, 0.08)
            : alpha(theme.palette.primary.main, 0.04),
      }
    : undefined,
}));

// One body row. Memoized so that when a parent re-renders with the SAME
// row object + columns array (e.g. a selection checkbox toggled elsewhere,
// with selection state delivered via context), the row's cells — often
// expensive sparklines / tooltips — are not re-rendered. Callers that
// rebuild ``columns`` every render simply fall back to the old behavior.
// CSS-only responsive hiding: below the given breakpoint the column
// disappears (header + cells) without re-rendering the memoized rows.
// ``bp`` is either an MUI breakpoint key ('sm'|'md'|'lg'|'xl') or a raw
// pixel threshold (number).
const hideBelowSx = (bp) => {
  if (!bp) return null;
  if (typeof bp === 'number') {
    return {
      display: 'none',
      [`@media (min-width:${bp}px)`]: { display: 'table-cell' },
    };
  }
  return { display: { xs: 'none', [bp]: 'table-cell' } };
};

// MUI default breakpoint pixel values — used to compute a responsive
// minWidth floor that matches whichever columns are actually visible.
const BP_PX = { xs: 0, sm: 600, md: 900, lg: 1200, xl: 1536 };
const bpToPx = (bp) => (typeof bp === 'number' ? bp : (BP_PX[bp] ?? 0));

const DataTableRow = React.memo(({ row, index, columns, onRowClick, dense }) => {
  const clickable = typeof onRowClick === 'function';
  return (
    <StyledRow
      clickable={clickable}
      onClick={clickable ? (e) => {
        // Skip if click originated inside a "stopRowClick" column or
        // any element that explicitly stops propagation (links,
        // buttons inside cells should call e.stopPropagation()).
        if (e.defaultPrevented) return;
        onRowClick(row);
      } : undefined}
    >
      {columns.map((col) => {
        const align = col.align || 'left';
        const cellSx = typeof col.cellSx === 'function'
          ? col.cellSx(row)
          : (col.cellSx || {});
        const content = col.render
          ? col.render(row, { index })
          : (row?.[col.key] ?? '—');
        return (
          <StyledCell
            key={col.key}
            align={align}
            padding={col.padding || 'normal'}
            onClick={col.stopRowClick ? (e) => e.stopPropagation() : undefined}
            sx={{
              width: col.width,
              ...(dense ? { padding: '5px 10px' } : null),
              ...hideBelowSx(col.hideBelow),
              ...cellSx,
            }}
          >
            {content}
          </StyledCell>
        );
      })}
    </StyledRow>
  );
});

const SortIcon = ({ active, direction }) => {
  if (!active) {
    return (
      <UnfoldMoreIcon
        fontSize="inherit"
        sx={{ ml: 0.5, opacity: 0.4, fontSize: 14, verticalAlign: 'middle' }}
      />
    );
  }
  return direction === 'asc' ? (
    <ArrowUpwardIcon
      fontSize="inherit"
      sx={{ ml: 0.5, fontSize: 14, verticalAlign: 'middle' }}
    />
  ) : (
    <ArrowDownwardIcon
      fontSize="inherit"
      sx={{ ml: 0.5, fontSize: 14, verticalAlign: 'middle' }}
    />
  );
};

const DataTable = ({
  columns,
  rows,
  getRowKey = (row, idx) => row?.uid ?? row?.id ?? row?._id ?? idx,
  onRowClick,
  loading = false,
  error = null,

  emptyState = null, // { icon, title, message, action }

  // Sort
  sortColumn,
  sortDirection,
  onSortChange, // (key, direction) => void

  // Pagination
  page,
  rowsPerPage,
  totalItems,
  onPageChange,         // (newPage) => void
  onRowsPerPageChange,  // (newRowsPerPage) => void
  rowsPerPageOptions = [10, 25, 50, 100],

  // Style
  stickyHeader = true,
  minHeight = 240,
  containerSx,
  // 'fixed' makes columns honour their declared widths regardless of
  // content, so long non-wrapping values (e.g. signal keys) truncate
  // with an ellipsis instead of pushing the whole table wider.
  tableLayout = 'auto',
  // Opt-in compact density: tighter cell padding so more rows fit on
  // screen (used by the Data Signals overview).
  dense = false,
}) => {
  const theme = useTheme();
  const showPagination =
    typeof page === 'number' &&
    typeof rowsPerPage === 'number' &&
    typeof totalItems === 'number' &&
    typeof onPageChange === 'function' &&
    typeof onRowsPerPageChange === 'function';

  // With a fixed layout the table no longer grows to fit content, so give
  // it a floor equal to the declared column widths — narrower viewports get
  // the horizontal scrollbar (as before) instead of crushed columns.
  // When columns use ``hideBelow`` the floor is responsive too: hidden
  // columns don't count toward the min width at breakpoints where they
  // are not rendered.
  const fixedMinWidth = React.useMemo(() => {
    if (tableLayout !== 'fixed') return undefined;
    const widthOf = (c) => (typeof c.width === 'number' ? c.width : 120);
    if (!columns.some((c) => c.hideBelow)) {
      return columns.reduce((acc, c) => acc + widthOf(c), 0);
    }
    // Build one media-query step per distinct hideBelow threshold so the
    // min-width floor always matches the set of visible columns.
    const thresholds = [...new Set(
      columns.filter((c) => c.hideBelow).map((c) => bpToPx(c.hideBelow)),
    )].sort((a, b) => a - b);
    const widthAt = (px) => columns.reduce((acc, c) => {
      if (c.hideBelow && bpToPx(c.hideBelow) > px) return acc;
      return acc + widthOf(c);
    }, 0);
    const sx = { minWidth: widthAt(0) };
    thresholds.forEach((px) => {
      sx[`@media (min-width:${px}px)`] = { minWidth: widthAt(px) };
    });
    return sx;
  }, [tableLayout, columns]);

  const handleHeaderClick = (col) => {
    if (!col.sortable || !onSortChange) return;
    if (sortColumn === col.key) {
      onSortChange(col.key, sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      onSortChange(col.key, 'desc');
    }
  };

  const renderBody = () => {
    if (loading && (!rows || rows.length === 0)) {
      return (
        <TableRow>
          <TableCell colSpan={columns.length} sx={{ borderBottom: 0 }}>
            <Box sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              minHeight,
              p: 4,
            }}>
              <CircularProgress size={32} color="primary" />
            </Box>
          </TableCell>
        </TableRow>
      );
    }

    if (error) {
      return (
        <TableRow>
          <TableCell colSpan={columns.length} sx={{ borderBottom: 0 }}>
            <Box sx={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              gap: 1,
              minHeight,
              p: 4,
              color: theme.palette.error.main,
            }}>
              <ErrorOutlineIcon />
              <Typography variant="body2">{String(error)}</Typography>
            </Box>
          </TableCell>
        </TableRow>
      );
    }

    if (!rows || rows.length === 0) {
      const fallback = {
        title: 'Nothing here yet',
        message: 'No records to display.',
        ...(emptyState || {}),
      };
      return (
        <TableRow>
          <TableCell colSpan={columns.length} sx={{ borderBottom: 0 }}>
            <Box sx={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              gap: 1,
              minHeight,
              p: 4,
              textAlign: 'center',
            }}>
              {fallback.icon ? (
                <Box sx={{ color: theme.palette.text.secondary, fontSize: 32 }}>
                  {fallback.icon}
                </Box>
              ) : null}
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                {fallback.title}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 480 }}>
                {fallback.message}
              </Typography>
              {fallback.action ? <Box sx={{ mt: 1 }}>{fallback.action}</Box> : null}
            </Box>
          </TableCell>
        </TableRow>
      );
    }

    return rows.map((row, idx) => (
      <DataTableRow
        key={getRowKey(row, idx)}
        row={row}
        index={idx}
        columns={columns}
        onRowClick={onRowClick}
        dense={dense}
      />
    ));
  };

  return (
    <Paper
      elevation={0}
      sx={{
        position: 'relative',
        bgcolor: theme.palette.background.paper,
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 2,
        overflow: 'hidden',
        ...containerSx,
      }}
    >
      <Box sx={{ overflowX: 'auto' }}>
        <Table size="small" stickyHeader={stickyHeader}
               sx={tableLayout === 'fixed'
                 ? {
                     tableLayout: 'fixed',
                     ...(typeof fixedMinWidth === 'number'
                       ? { minWidth: fixedMinWidth }
                       : fixedMinWidth),
                   }
                 : undefined}>
          <TableHead>
            <TableRow>
              {columns.map((col) => {
                const align = col.align || 'left';
                const isActive = sortColumn === col.key;
                return (
                  <StyledHeaderCell
                    key={col.key}
                    align={align}
                    padding={col.padding || 'normal'}
                    sortable={!!col.sortable}
                    onClick={() => handleHeaderClick(col)}
                    sx={{
                      width: col.width,
                      ...(dense ? { padding: '7px 10px' } : null),
                      ...hideBelowSx(col.hideBelow),
                      ...(col.headerSx || {}),
                    }}
                  >
                    <Box sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      // Keep header text and sort indicator on one line.
                    }}>
                      <span>{col.label}</span>
                      {col.sortable && (
                        <SortIcon active={isActive} direction={sortDirection} />
                      )}
                    </Box>
                  </StyledHeaderCell>
                );
              })}
            </TableRow>
          </TableHead>
          <TableBody>{renderBody()}</TableBody>
        </Table>
      </Box>

      {/* Subtle inline loader overlay when refetching with existing rows
          - keeps the user oriented during sort / filter / page changes. */}
      {loading && rows && rows.length > 0 && (
        <Box sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: 2,
          overflow: 'hidden',
        }}>
          <Box sx={{
            height: '100%',
            width: '40%',
            bgcolor: theme.palette.primary.main,
            opacity: 0.7,
            animation: 'datatable-loader 1.1s ease-in-out infinite',
            '@keyframes datatable-loader': {
              '0%':   { transform: 'translateX(-100%)' },
              '100%': { transform: 'translateX(350%)' },
            },
          }} />
        </Box>
      )}

      {showPagination && (
        <TablePagination
          component="div"
          count={totalItems}
          page={page}
          onPageChange={(_e, newPage) => onPageChange(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => onRowsPerPageChange(parseInt(e.target.value, 10))}
          rowsPerPageOptions={rowsPerPageOptions}
          sx={{
            borderTop: `1px solid ${theme.palette.divider}`,
            '& .MuiTablePagination-toolbar': { minHeight: 44, fontSize: 12 },
            '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
              fontSize: 12,
              color: theme.palette.text.secondary,
            },
            '& .MuiTablePagination-select': { fontSize: 12 },
          }}
        />
      )}
    </Paper>
  );
};

export default DataTable;
